import { useEffect, useState } from 'react';

import type { Types } from '@cornerstonejs/core';

import type { ImageFinding } from '../../../../utils/claimSummary/imageFindings';
import { ImageFindingBadge } from '../findingViewer/imageBrowser/imageBucket/image/ImageFindingBadge';

export interface CanvasDimensions {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface UseImageFindingOverlayProps {
  containerRef: React.RefObject<HTMLDivElement>;
  imageFindings: ImageFinding[];
  /** The cornerstone rendering engine instance, or false/null if not initialized */
  renderingEngine: Types.IRenderingEngine | false | null;
  viewportId?: string;
  imageId?: string;
  imageObjectId?: string;
}

function getCanvasAndImageDimensionsFromViewport(
  renderingEngine: Types.IRenderingEngine,
  viewportId: string,
): {
  canvasElement: HTMLCanvasElement;
  imageWidth: number;
  imageHeight: number;
} | null {
  const viewportForImage = renderingEngine.getViewport(viewportId) as
    | Types.IStackViewport
    | undefined;
  if (!viewportForImage) return null;
  const canvasElement = viewportForImage.canvas as HTMLCanvasElement;
  let imageWidth = 0;
  let imageHeight = 0;
  const imageData = viewportForImage.getImageData();
  if (imageData?.dimensions) {
    imageWidth = imageData.dimensions[0] || 0;
    imageHeight = imageData.dimensions[1] || 0;
  }
  return { canvasElement, imageWidth, imageHeight };
}

function getCanvasElementFromContainer(
  container: HTMLDivElement,
): HTMLCanvasElement | null {
  const cornerstoneElement = container.querySelector('.cornerstoneElement');
  return (
    (cornerstoneElement?.querySelector('canvas') as HTMLCanvasElement) ||
    (container.querySelector('canvas') as HTMLCanvasElement)
  );
}

function getImageDimensionsFromViewportMatch(
  renderingEngine: Types.IRenderingEngine,
  imageId: string,
): { imageWidth: number; imageHeight: number } | null {
  const viewports = renderingEngine.getViewports();
  const matchingViewport = viewports.find(
    (vp: Types.IStackViewport | Types.IVolumeViewport) =>
      vp.id.includes(imageId),
  );
  if (!matchingViewport) return null;
  const imageData = matchingViewport.getImageData();
  if (!imageData?.dimensions) return null;
  return {
    imageWidth: imageData.dimensions[0] || 0,
    imageHeight: imageData.dimensions[1] || 0,
  };
}

function computeDisplayedSizeAndOffsets(
  imageWidth: number,
  imageHeight: number,
  canvasRect: DOMRect,
): {
  displayedWidth: number;
  displayedHeight: number;
  offsetX: number;
  offsetY: number;
} {
  if (imageWidth > 0 && imageHeight > 0) {
    const imageAspect = imageWidth / imageHeight;
    const canvasAspect = canvasRect.width / canvasRect.height;
    if (imageAspect > canvasAspect) {
      const displayedWidth = canvasRect.width;
      const displayedHeight = canvasRect.width / imageAspect;
      return {
        displayedWidth,
        displayedHeight,
        offsetX: 0,
        offsetY: (canvasRect.height - displayedHeight) / 2,
      };
    }
    const displayedHeight = canvasRect.height;
    const displayedWidth = canvasRect.height * imageAspect;
    return {
      displayedWidth,
      displayedHeight,
      offsetX: (canvasRect.width - displayedWidth) / 2,
      offsetY: 0,
    };
  }
  return {
    displayedWidth: canvasRect.width,
    displayedHeight: canvasRect.height,
    offsetX: 0,
    offsetY: 0,
  };
}

function computeCanvasDimensionsForOverlay(
  container: HTMLDivElement | null,
  renderingEngine: Types.IRenderingEngine | false | null,
  viewportId?: string,
  imageId?: string,
): CanvasDimensions | null {
  if (!container) return null;
  try {
    let canvasElement: HTMLCanvasElement | null = null;
    let imageWidth = 0;
    let imageHeight = 0;

    if (renderingEngine && viewportId) {
      const fromViewport = getCanvasAndImageDimensionsFromViewport(
        renderingEngine,
        viewportId,
      );
      if (fromViewport) {
        canvasElement = fromViewport.canvasElement;
        imageWidth = fromViewport.imageWidth;
        imageHeight = fromViewport.imageHeight;
      }
    }

    if (!canvasElement) {
      canvasElement = getCanvasElementFromContainer(container);
      if (
        renderingEngine &&
        imageId &&
        imageWidth === 0 &&
        imageHeight === 0
      ) {
        const dims = getImageDimensionsFromViewportMatch(
          renderingEngine,
          imageId,
        );
        if (dims) {
          imageWidth = dims.imageWidth;
          imageHeight = dims.imageHeight;
        }
      }
    }

    if (!canvasElement) return null;

    const canvasRect = canvasElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (canvasRect.width === 0 || canvasRect.height === 0) return null;

    const { displayedWidth, displayedHeight, offsetX, offsetY } =
      computeDisplayedSizeAndOffsets(imageWidth, imageHeight, canvasRect);

    return {
      width: displayedWidth,
      height: displayedHeight,
      left: canvasRect.left - containerRect.left + offsetX,
      top: canvasRect.top - containerRect.top + offsetY,
    };
  } catch {
    return null;
  }
}

/**
 * Custom hook for tracking canvas dimensions and rendering image finding overlays.
 * Consolidates the duplicated logic from ImageLayoutContainer and FindingViewer.
 */
export const useImageFindingOverlay = ({
  containerRef,
  imageFindings,
  renderingEngine,
  viewportId,
  imageId,
  imageObjectId,
}: UseImageFindingOverlayProps) => {
  const [canvasDimensions, setCanvasDimensions] =
    useState<CanvasDimensions | null>(null);

  const hasFindings = imageFindings.length > 0;

  useEffect(() => {
    if (!containerRef.current || !hasFindings) {
      setCanvasDimensions(null);
      return;
    }

    const updateCanvasDimensions = () => {
      const dims = computeCanvasDimensionsForOverlay(
        containerRef.current,
        renderingEngine,
        viewportId,
        imageId,
      );
      setCanvasDimensions(dims);
    };

    updateCanvasDimensions();
    const timeoutId1 = setTimeout(updateCanvasDimensions, 100);
    const timeoutId2 = setTimeout(updateCanvasDimensions, 300);

    const resizeObserver = new ResizeObserver(updateCanvasDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updateCanvasDimensions);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCanvasDimensions);
    };
  }, [
    hasFindings,
    imageObjectId,
    imageId,
    renderingEngine,
    viewportId,
    containerRef,
  ]);

  const shouldShowOverlay =
    hasFindings &&
    canvasDimensions &&
    canvasDimensions.width > 0 &&
    canvasDimensions.height > 0;

  // Render the finding overlay JSX
  const FindingOverlay = shouldShowOverlay ? (
    <>
      {/* Finding badge - positioned at top of the actual image bounds */}
      <div
        className="vai-absolute vai-z-[100]"
        style={{
          left: `${canvasDimensions.left + 2}px`,
          top: `${canvasDimensions.top + 2}px`,
          width: `${canvasDimensions.width - 4}px`,
        }}
      >
        <ImageFindingBadge findings={imageFindings} />
      </div>
      {/* Border overlay - renders precisely over the image bounds */}
      <div
        className="vai-absolute vai-border-2 vai-border-amber-500 vai-rounded vai-pointer-events-none vai-z-[99]"
        style={{
          left: `${canvasDimensions.left + 2}px`,
          top: `${canvasDimensions.top + 2}px`,
          width: `${canvasDimensions.width - 4}px`,
          height: `${canvasDimensions.height - 4}px`,
        }}
      />
    </>
  ) : null;

  return {
    canvasDimensions,
    hasFindings,
    shouldShowOverlay,
    FindingOverlay,
  };
};
